package er.prateek.crossly.ui.notifications

import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import crossly.shared.generated.resources.Res
import crossly.shared.generated.resources.notif_soft_ask_body
import crossly.shared.generated.resources.notif_soft_ask_cta
import crossly.shared.generated.resources.notif_soft_ask_dismiss
import crossly.shared.generated.resources.notif_soft_ask_title
import er.prateek.crossly.ui.components.GameButton
import er.prateek.crossly.ui.components.GameButtonStyle
import er.prateek.crossly.ui.components.IconBell
import er.prateek.crossly.ui.components.IconFlame
import er.prateek.crossly.ui.theme.Radius
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game
import org.jetbrains.compose.resources.stringResource

/**
 * The themed re-ask shown after the OS permission has been denied once -- neither platform lets
 * an app re-trigger its own native prompt after that, so this is how a player who changes their
 * mind gets back to the system settings page. Styled with the same tokens as
 * [er.prateek.crossly.ui.share.NamePromptDialog]/[er.prateek.crossly.ui.coach.CoachOverlay]; a
 * `ModalBottomSheet` rather than an `AlertDialog` since this deserves more room for the
 * illustration than a dialog affords.
 *
 * The "GIF" the product ask called for is a Compose-drawn animated bell+flame instead of a real
 * GIF asset -- this codebase has no GIF-decoding/image-loading dependency (no Coil), so a
 * genuine animated asset would mean pulling one in just for this.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun NotificationPermissionSheet(
    onTurnOnReminders: () -> Unit,
    onDismiss: () -> Unit,
) {
    val colors = MaterialTheme.game

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = rememberModalBottomSheetState(),
        containerColor = colors.card,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = Spacing.xl, vertical = Spacing.lg),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(Spacing.lg),
        ) {
            ReminderIllustration()

            Text(
                text = stringResource(Res.string.notif_soft_ask_title),
                style = MaterialTheme.typography.titleLarge,
                color = colors.ink,
                textAlign = TextAlign.Center,
            )
            Text(
                text = stringResource(Res.string.notif_soft_ask_body),
                style = MaterialTheme.typography.bodyMedium,
                color = colors.inkMuted,
                textAlign = TextAlign.Center,
            )

            GameButton(
                text = stringResource(Res.string.notif_soft_ask_cta),
                onClick = onTurnOnReminders,
                style = GameButtonStyle.GOLD,
                fillWidth = true,
            )
            GameButton(
                text = stringResource(Res.string.notif_soft_ask_dismiss),
                onClick = onDismiss,
                style = GameButtonStyle.GHOST,
                fillWidth = true,
            )
        }
    }
}

/** A pulsing bell with a flame badge -- reuses [IconFlame]'s "streak" association at a glance. */
@Composable
private fun ReminderIllustration() {
    val colors = MaterialTheme.game
    val transition = rememberInfiniteTransition(label = "reminderIllustration")
    val scale by transition.animateFloat(
        initialValue = 0.94f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(tween(900), RepeatMode.Reverse),
        label = "bellScale",
    )

    Box(
        modifier = Modifier
            .size(96.dp)
            .graphicsLayer { scaleX = scale; scaleY = scale }
            .background(colors.accentSoft, CircleShape),
        contentAlignment = Alignment.Center,
    ) {
        IconBell(size = 48.dp, tint = colors.accent)
        Box(
            modifier = Modifier
                .align(Alignment.BottomEnd)
                .padding(4.dp)
                .size(30.dp)
                .background(colors.card, CircleShape)
                .padding(3.dp)
                .background(colors.goldBrush, CircleShape)
                .padding(4.dp),
        ) {
            IconFlame(size = 18.dp, tint = colors.onAccent)
        }
    }
}
